import { useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper,
  CircularProgress, Alert,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';

/**
 * Acceso al portal (PLAN_MASIVO_PORTAL): siempre con el correo. El código se
 * envía exista o no la cuenta (no se revela qué correos están registrados).
 * Al confirmarlo, el backend decide: cuenta con documento → entra; cuenta sin
 * documento → "Completa tus datos"; correo nuevo → "Completa tus datos" para
 * crear la cuenta (PortalIdentityStep).
 *
 * invitationEmail: si se llegó por el link de una invitación por correo, el
 * correo al que se envió, oculto (j***@gmail.com), para que entre con ese
 * (2026-10-09).
 */
const PortalAccessScreen = ({ position, onSubmit, initialEmail = '', invitationEmail = null }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail || '');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setError(null);

    const value = email.trim().toLowerCase();
    if (!value) {
      setEmailError(t('portal.emailRequired'));
      return;
    }
    if (!isValidEmail(value)) {
      setEmailError(t('portal.emailInvalid'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post(`/public/candidates/email/request-otp`, { email: value });
      if (response.data.response?.status_response === 'OK') {
        onSubmit({ mode: 'email', email: value, maskedEmail: null });
      } else {
        setError(t('portal.otpSendError'));
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.status === 429 ? t('portal.tooManyRequests') : t('portal.otpSendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', px: 2, pt: { xs: '6vh', md: '10vh' } }}>
      <Box sx={{ width: '100%', maxWidth: 480 }}>

        {/* Logo / Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/amandalogoh.png"
            alt="Amanda HR"
            sx={{ height: 40, mb: 3 }}
          />
          {position ? (
            <>
              <Typography variant="h5" fontWeight={700}>
                {position.name}
              </Typography>
              {position.industria && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {position.industria}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="h5" fontWeight={700}>
              {t('portal.title')}
            </Typography>
          )}
        </Box>

        <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            {t('portal.emailAccess.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('portal.emailAccess.subtitle')}
          </Typography>

          {invitationEmail && (
            <Alert severity="info" sx={{ mb: 2 }}>{t('portal.invitation.accessHint', { email: invitationEmail })}</Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              label={t('portal.emailLabel')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
              fullWidth
              required
              type="email"
              autoComplete="email"
              error={!!emailError}
              helperText={emailError}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5 }}
            >
              {loading ? t('portal.sending') : t('portal.continue')}
            </Button>
          </Box>
        </Paper>

      </Box>
    </Box>
  );
};

export default PortalAccessScreen;
